import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useSite } from '../context/SiteContext';
import { toast } from 'react-hot-toast';

const Capture = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const { getCurrentSiteId, currentSite } = useSite();
  const navigate = useNavigate();
  const [lines, setLines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [defectTypes, setDefectTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    line: '',
    shift: '',
    product_code: '',
    defect_type: '',
    quantity: 1,
    notes: ''
  });

  // Defects list
  const [defects, setDefects] = useState([]);
  const [defectsPage, setDefectsPage] = useState(1);
  const DEFECTS_PAGE_SIZE = 20;
  const fileInputRef = React.useRef(null);
  // Filtros
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    line: '',
    shift: '',
    product: '',
    defect_type: '',
    notes: ''
  });
  // Edición inline
  const [editingId, setEditingId] = useState(null);
  const [editRow, setEditRow] = useState(null);

  const fetchCatalogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Ensure Authorization header is present even if axios defaults
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const params = {};
      const siteId = getCurrentSiteId();
      if (siteId) {
        params.site_id = siteId;
      }

      // Fetch all catalogs in one shot (lighter and avoids partial failures)
  const { data } = await axios.get('/catalogs/all', { headers, params });

      setLines(data.lines || []);
      setShifts(data.shifts || []);
      setDefectTypes(data.defect_types || []);
    } catch (err) {
      console.error('Error fetching catalogs:', err);
      let errorMessage = t('capture.messages.loadError');
      
      if (err.response?.status === 401) {
        errorMessage = t('capture.messages.sessionExpired');
        setTimeout(() => navigate('/login'), 2000);
      } else if (err.response?.status === 403) {
        errorMessage = t('capture.messages.noPermissions');
      } else if (!err.response) {
        errorMessage = t('capture.messages.connectionError');
      }
      // Attach more detail for troubleshooting
      const detail = err.response?.data?.detail || err.message;
      setError(`${errorMessage}${detail ? ` Detalle: ${detail}` : ''}`);
    } finally {
      setLoading(false);
    }
  }, [navigate, t, getCurrentSiteId]);

  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated()) {
        navigate('/login');
        return;
      }
      fetchCatalogs();
    }
  }, [authLoading, isAuthenticated, navigate, fetchCatalogs]);

  // Recargar catálogos cuando cambie el sitio
  useEffect(() => {
    if (!authLoading && isAuthenticated() && currentSite) {
      fetchCatalogs();
      fetchDefects();
    }
  }, [currentSite, authLoading, isAuthenticated, fetchCatalogs]);

  const fetchDefects = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const params = {};
      const siteId = getCurrentSiteId();
      if (siteId) {
        params.site_id = siteId;
      }
      
      const { data } = await axios.get('/defects/', { headers, params });
      // Ensure newest defects appear first (stack-like): prefer numeric id desc, otherwise date desc
      const sortDefectsDesc = (arr) => {
        if (!Array.isArray(arr)) return arr;
        return arr.slice().sort((a, b) => {
          if (a?.id != null && b?.id != null) return b.id - a.id;
          const da = a?.date ? new Date(a.date) : null;
          const db = b?.date ? new Date(b.date) : null;
          if (da && db) return db - da;
          return 0;
        });
      };

      setDefects(sortDefectsDesc(data));
      setDefectsPage(1); // reset página al refrescar
    } catch (err) {
      console.error('Error fetching defects:', err);
    }
  };

  useEffect(() => {
    if (!authLoading && isAuthenticated()) {
      fetchDefects();
    }
  }, [authLoading, isAuthenticated]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Basic validation
    if (!form.date || !form.line || !form.shift || !form.product_code || !form.defect_type || !form.quantity) {
      toast.error(t('capture.messages.completeFields'));
      return;
    }
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const siteId = getCurrentSiteId();
      const payload = {
        date: form.date,
        line: form.line,
        shift: form.shift,
        product_code: form.product_code,
        defect_type: form.defect_type,
        quantity: Number(form.quantity),
        notes: form.notes || null,
        site_id: siteId
      };
      const { data } = await axios.post('/defects/', payload, { headers });
      toast.success(t('capture.messages.success'));
      // Push to top and reset some fields
      setDefects((prev) => [data, ...prev]);
      setForm((prev) => ({ ...prev, quantity: 1, notes: '' }));
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`No se pudo guardar. ${detail}`);
      if (err.response?.status === 401) navigate('/login');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditRow({
      id: row.id,
      date: row.date?.slice ? row.date.slice(0,10) : row.date,
      line: row.line_code || row.line || row.line_name,
      shift: row.shift_code || row.shift || row.shift_name,
      product_code: row.product_code,
      defect_type: row.defect_type_name || row.defect_type,
      quantity: row.quantity,
      notes: row.notes || ''
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditRow(null); };

  const saveEdit = async () => {
    if (!editRow) return;
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const payload = {
        date: editRow.date,
        line: editRow.line,
        shift: editRow.shift,
        product_code: editRow.product_code,
        defect_type: editRow.defect_type,
        quantity: Number(editRow.quantity),
        notes: editRow.notes || null
      };
      const { data } = await axios.put(`/defects/${editRow.id}`, payload, { headers });
      setDefects((prev) => prev.map(d => d.id === editRow.id ? { ...d, ...data } : d));
      toast.success(t('capture.messages.updateSuccess'));
      cancelEdit();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('capture.messages.error'));
    }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(t('capture.messages.confirmDelete'))) return;
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`/defects/${row.id}`, { headers });
      setDefects(prev => prev.filter(d => d.id !== row.id));
      toast.success(t('capture.messages.deleteSuccess'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(f => ({ ...f, [name]: value }));
    setDefectsPage(1);
  };

  const filteredDefects = useMemo(() => {
    return defects.filter(d => {
      const dateStr = typeof d.date === 'string' ? d.date : (d.date?.slice ? d.date.slice(0,10) : '');
      if (filters.dateFrom && dateStr < filters.dateFrom) return false;
      if (filters.dateTo && dateStr > filters.dateTo) return false;
      const lineVal = (d.line_code || d.line || '').toLowerCase();
      if (filters.line && !lineVal.includes(filters.line.toLowerCase())) return false;
      const shiftVal = (d.shift_code || d.shift || '').toLowerCase();
      if (filters.shift && !shiftVal.includes(filters.shift.toLowerCase())) return false;
      const prodVal = (d.product_code || '').toLowerCase();
      if (filters.product && !prodVal.includes(filters.product.toLowerCase())) return false;
      const defectVal = (d.defect_type_name || d.defect_type || '').toLowerCase();
      if (filters.defect_type && !defectVal.includes(filters.defect_type.toLowerCase())) return false;
      const notesVal = (d.notes || '').toLowerCase();
      if (filters.notes && !notesVal.includes(filters.notes.toLowerCase())) return false;
      return true;
    });
  }, [defects, filters]);

  const pagedDefects = useMemo(() => {
    return filteredDefects.slice((defectsPage - 1) * DEFECTS_PAGE_SIZE, defectsPage * DEFECTS_PAGE_SIZE);
  }, [filteredDefects, defectsPage]);

  const isAdmin = user?.role === 'admin';

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Incluir site_id en la exportación
      const siteId = getCurrentSiteId();
      const params = {};
      if (siteId) {
        params.site_id = siteId;
      }
      
      const res = await axios.get('/defects/export.csv', { headers, params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'defects.csv');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export error:', e);
    }
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const formData = new FormData();
      formData.append('file', file);
      
      // Incluir site_id en la importación
      const siteId = getCurrentSiteId();
      if (siteId) {
        formData.append('site_id', siteId);
      }
      
      const res = await axios.post('/defects/import', formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      // Refresh list regardless of partial failures
      await fetchDefects();
      if (res.data?.success) {
        toast.success(`${t('capture.messages.imported')}: ${res.data.imported_count}`);
      } else {
        toast((toastInstance) => (
          <div>
            <div>{t('capture.messages.imported')}: {res.data?.imported_count || 0}</div>
            <div>{t('capture.messages.failed')}: {res.data?.failed_rows?.length || 0}</div>
          </div>
        ));
      }
    } catch (err) {
      console.error('Import error:', err);
      toast.error(err.response?.data?.detail || t('capture.messages.error'));
    } finally {
      e.target.value = '';
    }
  };

  if (authLoading) {
    return (
      <div className="flex justify-center items-center h-52">
        <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-500 border-solid"></div>
        <span className="ml-4 text-blue-700 font-semibold">{t('capture.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-800 rounded-xl p-6 max-w-xl mx-auto mt-8 shadow-lg">
        <h5 className="font-bold text-lg mb-2">{t('capture.messages.loadError')}</h5>
        <p className="mb-4">{error}</p>
        <div className="flex gap-3">
          {!error.includes('Redirigiendo') && !error.includes('Redirecting') && (
            <button className="px-4 py-2 bg-white border border-red-400 text-red-700 rounded hover:bg-red-50 font-semibold transition" onClick={fetchCatalogs}>
              {t('capture.messages.retry')}
            </button>
          )}
          {(error.includes('sesión') || error.includes('Session')) && (
            <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-semibold transition" onClick={() => navigate('/login')}>
              {t('capture.messages.goToLogin')}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-52">
        <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-500 border-solid"></div>
        <span className="ml-4 text-blue-700 font-semibold">{t('capture.loadingCatalogs')}</span>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <h1 className="text-3xl font-extrabold text-blue-900 mb-2">{t('capture.title')}</h1>
      <p className="text-gray-700 mb-6">{t('capture.subtitle')}</p>

      {/* Formulario de Captura */}
      <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-blue-100">
        <h2 className="text-xl font-bold text-blue-800 mb-6">{t('capture.formTitle')}</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">{t('capture.form.date')}</label>
              <input name="date" type="date" className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400" value={form.date} onChange={handleChange} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">{t('capture.form.line')}</label>
              <select name="line" className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400" value={form.line} onChange={handleChange}>
                <option value="">{t('capture.form.selectLine')}</option>
                {lines.map(line => (
                  <option key={line.id} value={line.code}>{line.name} ({line.code})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">{t('capture.form.shift')}</label>
              <select name="shift" className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400" value={form.shift} onChange={handleChange}>
                <option value="">{t('capture.form.selectShift')}</option>
                {shifts.map(shift => (
                  <option key={shift.id} value={shift.code}>{shift.name} ({shift.code})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">{t('capture.form.product')}</label>
              <input name="product_code" type="text" className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400" placeholder={t('capture.form.productPlaceholder')} value={form.product_code} onChange={handleChange} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">{t('capture.form.defectType')}</label>
              <select name="defect_type" className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400" value={form.defect_type} onChange={handleChange}>
                <option value="">{t('capture.form.selectDefectType')}</option>
                {defectTypes.map(defectType => (
                  <option key={defectType.id} value={defectType.name}>{defectType.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">{t('capture.form.quantity')}</label>
              <input name="quantity" type="number" min="1" className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400" value={form.quantity} onChange={handleChange} />
            </div>
            <div className="flex items-end">
              <button type="submit" className="w-full bg-blue-700 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded-lg shadow transition disabled:opacity-60" disabled={saving}>
                {saving ? t('capture.form.saving') : t('capture.form.saveButton')}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">{t('capture.form.notes')}</label>
            <textarea name="notes" className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400" rows="3" value={form.notes} onChange={handleChange}></textarea>
          </div>
        </form>
      </div>

      {/* Tabla de Defectos */}
      <div className="bg-white rounded-2xl shadow-lg p-8 border border-blue-100">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
          <h2 className="text-xl font-bold text-blue-800">{t('capture.tableTitle')}</h2>
          <div className="flex gap-2">
            <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleImport} />
            <button className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-300 rounded-lg font-semibold hover:bg-blue-100 transition" type="button" onClick={handleImportClick}>{t('capture.table.importCSV')}</button>
            <button className="px-4 py-2 bg-green-50 text-green-700 border border-green-300 rounded-lg font-semibold hover:bg-green-100 transition" type="button" onClick={handleExport}>{t('capture.table.exportCSV')}</button>
          </div>
        </div>
        {/* Filtros */}
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{t('capture.filters.from')}</label>
            <input type="date" name="dateFrom" value={filters.dateFrom} onChange={handleFilterChange} className="w-full rounded border border-gray-300 px-2 py-1 text-gray-800 text-sm" />
          </div>
            <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{t('capture.filters.to')}</label>
            <input type="date" name="dateTo" value={filters.dateTo} onChange={handleFilterChange} className="w-full rounded border border-gray-300 px-2 py-1 text-gray-800 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{t('capture.filters.line')}</label>
            <input type="text" name="line" value={filters.line} onChange={handleFilterChange} placeholder={t('capture.filters.searchLine')} className="w-full rounded border border-gray-300 px-2 py-1 text-gray-800 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{t('capture.filters.shift')}</label>
            <input type="text" name="shift" value={filters.shift} onChange={handleFilterChange} placeholder={t('capture.filters.searchShift')} className="w-full rounded border border-gray-300 px-2 py-1 text-gray-800 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{t('capture.filters.product')}</label>
            <input type="text" name="product" value={filters.product} onChange={handleFilterChange} placeholder={t('capture.filters.searchProduct')} className="w-full rounded border border-gray-300 px-2 py-1 text-gray-800 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{t('capture.filters.defectType')}</label>
            <input type="text" name="defect_type" value={filters.defect_type} onChange={handleFilterChange} placeholder={t('capture.filters.searchDefectType')} className="w-full rounded border border-gray-300 px-2 py-1 text-gray-800 text-sm" />
          </div>
          <div className="md:col-span-2 lg:col-span-2">
            <label className="block text-xs font-semibold text-gray-600 mb-1">{t('capture.filters.notes')}</label>
            <input type="text" name="notes" value={filters.notes} onChange={handleFilterChange} placeholder={t('capture.filters.searchNotes')} className="w-full rounded border border-gray-300 px-2 py-1 text-gray-800 text-sm" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left border border-gray-200 rounded-xl">
            <thead className="bg-blue-100 text-blue-900">
              <tr>
                <th className="px-4 py-2 font-bold">{t('capture.table.date')}</th>
                <th className="px-4 py-2 font-bold">{t('capture.table.line')}</th>
                <th className="px-4 py-2 font-bold">{t('capture.table.shift')}</th>
                <th className="px-4 py-2 font-bold">{t('capture.table.product')}</th>
                <th className="px-4 py-2 font-bold">{t('capture.table.defectType')}</th>
                <th className="px-4 py-2 font-bold">{t('capture.table.quantity')}</th>
                <th className="px-4 py-2 font-bold">{t('capture.table.notes')}</th>
                <th className="px-4 py-2 font-bold">{t('capture.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredDefects.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center text-gray-400 py-6">{t('capture.table.noData')}</td>
                </tr>
              ) : (
                pagedDefects.map((d) => {
                  const isEditing = editingId === d.id;
                  return (
                    <tr key={d.id} className="even:bg-gray-50">
                      <td className="px-4 py-2">{isEditing ? <input type="date" className="border rounded px-2 py-1" value={editRow.date} onChange={e=>setEditRow(r=>({...r,date:e.target.value}))} /> : (typeof d.date === 'string' ? d.date : (d.date?.slice ? d.date.slice(0,10) : d.date))}</td>
                      <td className="px-4 py-2">{isEditing ? <input className="border rounded px-2 py-1 w-24" value={editRow.line} onChange={e=>setEditRow(r=>({...r,line:e.target.value}))} /> : (d.line_name || d.line || d.line_code)}</td>
                      <td className="px-4 py-2">{isEditing ? <input className="border rounded px-2 py-1 w-20" value={editRow.shift} onChange={e=>setEditRow(r=>({...r,shift:e.target.value}))} /> : (d.shift_name || d.shift || d.shift_code)}</td>
                      <td className="px-4 py-2">{isEditing ? <input className="border rounded px-2 py-1 w-28" value={editRow.product_code} onChange={e=>setEditRow(r=>({...r,product_code:e.target.value}))} /> : (d.product_name || d.product_code)}</td>
                      <td className="px-4 py-2">{isEditing ? <input className="border rounded px-2 py-1 w-40" value={editRow.defect_type} onChange={e=>setEditRow(r=>({...r,defect_type:e.target.value}))} /> : (d.defect_type_name || d.defect_type)}</td>
                      <td className="px-4 py-2">{isEditing ? <input type="number" min="1" className="border rounded px-2 py-1 w-20" value={editRow.quantity} onChange={e=>setEditRow(r=>({...r,quantity:e.target.value}))} /> : d.quantity}</td>
                      <td className="px-4 py-2">{isEditing ? <input className="border rounded px-2 py-1 w-40" value={editRow.notes} onChange={e=>setEditRow(r=>({...r,notes:e.target.value}))} /> : (d.notes || '')}</td>
                      <td className="px-4 py-2 text-gray-600">
                        {isAdmin ? (
                          isEditing ? (
                            <div className="flex gap-2">
                              <button onClick={saveEdit} className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700">{t('capture.table.save')}</button>
                              <button onClick={cancelEdit} className="px-2 py-1 bg-gray-300 text-gray-800 rounded text-xs hover:bg-gray-400">{t('capture.table.cancel')}</button>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <button onClick={()=>startEdit(d)} className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700">{t('capture.table.edit')}</button>
                              <button onClick={()=>deleteRow(d)} className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700">{t('capture.table.delete')}</button>
                            </div>
                          )
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {/* Controles de paginación */}
        {filteredDefects.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
            {(() => {
              const total = filteredDefects.length;
              const start = (defectsPage - 1) * DEFECTS_PAGE_SIZE + 1;
              const end = Math.min(defectsPage * DEFECTS_PAGE_SIZE, total);
              const totalPages = Math.max(1, Math.ceil(total / DEFECTS_PAGE_SIZE));
              return (
                <>
                  <div className="text-gray-700 text-sm">{t('capture.pagination.showing')} {start}-{end} {t('capture.pagination.of')} {total}</div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                      onClick={() => setDefectsPage((p) => Math.max(1, p - 1))}
                      disabled={defectsPage <= 1}
                    >
                      {t('capture.pagination.previous')}
                    </button>
                    <span className="text-sm text-gray-700">{t('capture.pagination.page')} {defectsPage} {t('capture.pagination.of')} {totalPages}</span>
                    <button
                      type="button"
                      className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                      onClick={() => setDefectsPage((p) => Math.min(totalPages, p + 1))}
                      disabled={defectsPage >= totalPages}
                    >
                      {t('capture.pagination.next')}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default Capture;